import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Camera,
  Wrench,
  Shield,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { label: 'Clientes', to: '/clients', icon: Users },
  { label: 'CCTV', to: '/cctv', icon: Camera },
  { label: 'Mantenimientos', to: '/maintenance', icon: Wrench },
  { label: 'Usuarios', to: '/users', icon: Shield, adminOnly: true },
  { label: 'Configuración', to: '/settings', icon: Settings },
];

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const visibleNavItems = navItems.filter(
    (item) => !item.adminOnly || user?.role === 'admin'
  );

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center justify-between px-4">
        {!collapsed && (
          <span className="text-lg font-bold text-surface-900 whitespace-nowrap">
            Mantenimientos
          </span>
        )}
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="rounded-md p-1 text-surface-400 hover:bg-surface-100 hover:text-surface-600 md:hidden transition-colors"
          aria-label="Cerrar menú"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 space-y-1 px-2 py-2">
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                active
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <Icon
                className={`h-5 w-5 flex-shrink-0 ${
                  active ? 'text-primary-600' : 'text-surface-400 group-hover:text-surface-600'
                } ${collapsed ? '' : 'mr-3'}`}
                aria-hidden="true"
              />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-surface-200 p-4">
        {!collapsed && (
          <div className="mb-3 truncate">
            <p className="text-sm font-medium text-surface-900">{user?.name}</p>
            <p className="text-xs capitalize text-surface-500">{user?.role}</p>
          </div>
        )}
        <button
          type="button"
          onClick={logout}
          className={`group flex items-center rounded-lg px-3 py-2.5 text-sm font-medium text-surface-600 hover:bg-surface-100 hover:text-surface-900 transition-all ${
            collapsed ? 'justify-center w-full' : 'w-full'
          }`}
          title={collapsed ? 'Cerrar sesión' : undefined}
        >
          <LogOut className={`h-5 w-5 flex-shrink-0 text-surface-400 group-hover:text-surface-600 ${
            collapsed ? '' : 'mr-3'
          }`} />
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-surface-50">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex md:flex-col border-r border-surface-200 bg-white transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {sidebarContent}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute bottom-20 -right-3 hidden h-6 w-6 items-center justify-center rounded-full border border-surface-200 bg-white text-surface-400 shadow-sm hover:text-surface-600 md:flex transition-colors"
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" />
          )}
        </button>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden transition-opacity duration-200"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-xl transition-transform duration-300 md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top header */}
        <header className="flex h-16 flex-shrink-0 items-center border-b border-surface-200 bg-white px-4 shadow-sm">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="rounded-md p-2 text-surface-500 hover:bg-surface-100 hover:text-surface-700 md:hidden transition-colors"
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-right sm:block">
              <p className="text-sm font-medium text-surface-900">{user?.name}</p>
              <p className="text-xs capitalize text-surface-500">{user?.role}</p>
            </span>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <button
              type="button"
              onClick={logout}
              className="rounded-md p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
              aria-label="Cerrar sesión"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
